import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Table, Button, Space, Tag, Modal, Form, Input, Select, message } from 'antd';
import api from '../../../utils/api';

const { Title, Paragraph } = Typography;
const { Option } = Select;

const AdminsList = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Name', dataIndex: 'full_name', key: 'full_name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Role', dataIndex: 'role', key: 'role', width: 160, render: (r) => <Tag color={r === 'superadmin' ? 'magenta' : 'blue'}>{r || '—'}</Tag> },
    {
      title: 'Actions', key: 'actions', width: 200, render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => onEdit(record)}>Edit</Button>
          <Button size="small" danger onClick={() => onDisable(record)}>Delete</Button>
        </Space>
      )
    }
  ];

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        // Fetch admins and staff (backend supports filtering by role; here we fetch all then filter)
        const resp = await api.get('/users?limit=200');
        const items = Array.isArray(resp?.items) ? resp.items : [];
        const filtered = items.filter(u => u.role === 'superadmin' || u.role === 'staff');
        setData(filtered);
      } catch (e) {
        message.error(e.message || 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const onCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ role: 'staff' });
    setOpen(true);
  };

  const onEdit = (record) => {
    setEditing(record);
    form.setFieldsValue(record);
    setOpen(true);
  };

  const onDisable = async (record) => {
    try {
      await api.delete(`/users/${record.id}`);
      setData(prev => prev.filter(u => u.id !== record.id));
      message.success('User deleted');
    } catch (e) {
      message.error(e.message || 'Failed to delete');
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        const payload = { full_name: values.full_name, email: values.email, role: values.role };
        if (values.password) payload.password = values.password;
        const updated = await api.put(`/users/${editing.id}`, payload);
        setData(prev => prev.map(u => u.id === editing.id ? updated : u));
        message.success('User updated');
      } else {
        const payload = { full_name: values.full_name, email: values.email, role: values.role, password: values.password };
        const created = await api.post('/users', payload);
        setData(prev => [created, ...prev]);
        message.success('User created');
      }
      setOpen(false);
      setEditing(null);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[
        { title: 'Admin' },
        { title: 'Users' },
        { title: 'Super Admins & Staff' },
      ]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Super Admins & Staff</Title>
        <Space>
          <Button type="primary" onClick={onCreate}>Invite Admin</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Manage super admins and staff accounts.
        </Paragraph>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        open={open}
        title={editing ? 'Edit Admin' : 'Invite Admin'}
        onCancel={() => { setOpen(false); setEditing(null); }}
        onOk={onSubmit}
        confirmLoading={saving}
      >
        <Form layout="vertical" form={form}>
          <Form.Item label="Name" name="full_name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Role" name="role" rules={[{ required: true }]}>
            <Select>
              <Option value="superadmin">Super Admin</Option>
              <Option value="staff">Staff</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Password" name="password" rules={editing ? [] : [{ required: true, min: 6 }]}>\n            <Input.Password placeholder={editing ? 'Leave blank to keep current password' : 'Set a temporary password'} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminsList;
